/**
 * The API client.
 *
 * The API is served from the same origin as this app (Traefik routes /api to the
 * api service), so requests are relative and the session cookie travels on its own.
 * There is no token to store, no Authorization header to build, and nothing about
 * the session is reachable from JavaScript — which is the point: the cookie is
 * HttpOnly, so an XSS bug cannot walk off with a session.
 *
 * Every call goes through `request`, so error handling exists in exactly one place.
 * The API answers with `{ error: "code" }` on failure; a code is not something to
 * show a person, so `ApiError` carries the code for logic and a translated message
 * for display.
 */

import { GRADE_NAMES, LISTING_MODE_NAMES } from "./data.jsx";

// Keyed by the codes the API actually sends. Verified against the routes rather
// than guessed: a message table full of codes that never arrive is a table that
// silently shows "something went wrong" for every real failure.
const MESSAGES = {
  'en-US': {
    invalid_credentials: 'That email and password do not match.',
    email_unavailable: 'There is already an account with that email.',
    email_not_verified: 'Confirm your email address first — check your inbox.',
    weak_password: 'That password is too short. Twelve characters or more.',
    not_authenticated: 'Sign in to do that.',
    not_found: 'That is gone.',
    invalid_or_expired: 'That link has expired. Ask for a new one.',
    already_verified: 'That address is already confirmed.',
    price_required_for_sale: 'A card for sale needs a price.',
    /*
     * A TRADE WITH A SHOP HAPPENS AT ITS COUNTER, in its opening hours.
     *
     * Neither of these should ever reach a reader: the form offers the counter
     * and nothing else, and refuses a shut hour before sending. They are here
     * for the tab left open while a shop was being verified — without them the
     * toast would say "something went wrong", which is the sentence that makes
     * somebody retry the same thing.
     */
    meet_at_shop_counter: 'That trade happens at the store counter.',
    shop_is_closed_then: 'The store is closed then. Pick a time inside its opening hours.',
    price_not_allowed_for_trade: 'A card for trade cannot have a price.',
    // The form never sends a listing without a printing since 2026-10-12; this
    // is for a tab opened before that rule, which would otherwise read "unknown".
    listing_needs_a_catalog_product: 'Pick the card from the list first.',
    // Sealed product and online codes: the list never offers one, so this is
    // for a tab opened before migration 153, or a want made from an old link.
    product_not_listable: 'Only physical cards can be listed or wanted here.',
    // Both of these are answered properly on screen — the accept button says how
    // many are left, and the stock dialog lists the trades holding the copies.
    // These are the fallback for anywhere that only has room for a sentence.
    insufficient_stock: 'There are not that many copies left.',
    stock_below_committed: 'Some copies are already promised to accepted trades.',
    // A trade on a listing taken down since the screen loaded (ticket 14).
    listing_not_active: 'That listing was withdrawn, so the trade can no longer be agreed.',
    // The app sends no reason; this is for a caller that typed one of ours.
    reserved_reason: 'That reason is one the app writes. Use other words.',
    // A listing synced from the shop's CardTrader stock: every edit to it is
    // refused (ticket 11). The screen offers none; this is for a tab opened
    // before the sync.
    managed_on_cardtrader: 'This card is managed on CardTrader. Change it there.',
    no_changes: 'Nothing to change.',
    /*
     * The cap, as a fallback nobody should normally see: the dialog stops
     * offering Add at `MAX_LOCATIONS` and says so in its own sentence, with the
     * number interpolated. This table cannot interpolate, so the words are
     * spelled out — which is why it is the fallback and not the message.
     */
    location_limit_reached: 'You can save up to five locations. Delete one first.',
    label_required: 'Give the location a name.',
    // No screen a shop sees offers to move it; this is the fallback for a
    // stale tab. A shop moves by moving its counter, on its own page.
    shop_moves_with_its_counter: 'A store is where its counter is. Move the store’s address instead.',
    no_email: 'No email address on the account.',
    rate_limited: 'Too many attempts. Give it a few minutes and try again.',
    /*
     * CardTrader, or Cloudflare in front of it, refusing US for a while: the
     * challenge our own daily volume earns, which lifts by itself in minutes.
     * Nothing the shop can do helps, so this says WAIT and never "check your
     * token". The connect screen has its own fuller sentence (the token was
     * not stored); this is the fallback for the webhook button and anywhere
     * else the code arrives.
     */
    upstream_unavailable: 'CardTrader is not answering right now. Try again in a few minutes.',
    offline: 'No connection. Check your network and try again.',
    /*
     * The answer arrived and was not readable. Says RELOAD, because the thing
     * that eats a response between the server and here is usually a stale
     * service worker, and a reload is what replaces one.
     */
    unreadable: 'The answer came back empty. Reload the page and try again.',
    /*
     * Whatever the server got wrong, by the only name the API gives it: the
     * real error is in its log, and a database error never reaches a client.
     */
    internal_error: 'Something went wrong on our side. Try again.',
    unknown: 'Something went wrong. Try again.',
  },
  'es-ES': {
    invalid_credentials: 'Ese correo y esa contraseña no coinciden.',
    email_unavailable: 'Ya hay una cuenta con ese correo.',
    email_not_verified: 'Confirma tu correo primero — mira tu bandeja de entrada.',
    weak_password: 'Esa contraseña es muy corta. Doce caracteres o más.',
    not_authenticated: 'Inicia sesión para hacer eso.',
    not_found: 'Eso ya no está.',
    invalid_or_expired: 'Ese enlace ha caducado. Pide uno nuevo.',
    already_verified: 'Ese correo ya está confirmado.',
    price_required_for_sale: 'Una carta en venta necesita precio.',
    meet_at_shop_counter: 'Ese cambio se hace en el mostrador de la tienda.',
    shop_is_closed_then: 'La tienda está cerrada a esa hora. Elige una dentro de su horario.',
    price_not_allowed_for_trade: 'Una carta para cambiar no lleva precio.',
    listing_needs_a_catalog_product: 'Elige antes la carta en la lista.',
    product_not_listable: 'Aquí solo se anuncian y se buscan cartas físicas.',
    insufficient_stock: 'No quedan tantas copias.',
    stock_below_committed: 'Algunas copias ya están comprometidas en cambios aceptados.',
    listing_not_active: 'Ese anuncio se retiró, así que el cambio ya no se puede cerrar.',
    reserved_reason: 'Ese motivo lo escribe la aplicación. Usa otras palabras.',
    managed_on_cardtrader: 'Esta carta se gestiona en CardTrader. Cámbiala allí.',
    no_changes: 'No hay nada que cambiar.',
    location_limit_reached: 'Puedes guardar hasta cinco ubicaciones. Borra una antes.',
    label_required: 'Ponle un nombre a la ubicación.',
    shop_moves_with_its_counter: 'Una tienda está donde está su mostrador. Cambia la dirección de la tienda.',
    no_email: 'La cuenta no tiene correo.',
    rate_limited: 'Demasiados intentos. Espera unos minutos y vuelve a probar.',
    upstream_unavailable: 'CardTrader no responde ahora mismo. Vuelve a probar dentro de unos minutos.',
    offline: 'Sin conexión. Revisa tu red y vuelve a probar.',
    unreadable: 'La respuesta ha llegado vacía. Recarga la página y vuelve a probar.',
    internal_error: 'Algo ha fallado por nuestra parte. Vuelve a probar.',
    unknown: 'Algo ha fallado. Vuelve a probar.',
  },
};

/** Spanish is the fallback, not English. */
let locale = 'es-ES';
const setLocale = (l) => { locale = MESSAGES[l] ? l : 'es-ES'; };

class ApiError extends Error {
  constructor(code, status, detail, payload) {
    const table = MESSAGES[locale] || MESSAGES['es-ES'];
    super(table[code] || detail || table.unknown);
    this.code = code;
    this.status = status;
    /*
     * The whole refusal body, not just its code.
     *
     * Most failures are a code and nothing else, and a code is all a toast
     * needs. Some are not: refusing a stock reduction has to say WHICH accepted
     * trades are holding the copies, or the seller is told "no" with no way to
     * find the thing blocking them. Dropping the body here meant that even
     * where the API answered properly, the client could not read the answer.
     */
    this.payload = payload || null;
  }
}

async function request(path, { method = 'GET', body, form, raw, signal, fresh } = {}) {
  let response;
  try {
    response = await fetch(path, {
      method,
      // Same origin, but stated explicitly: this is the line that makes the session
      // work, and it should be obvious rather than inherited from a default.
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        'accept-language': locale,
        // FormData sets its own content-type, boundary included. Adding one here
        // would overwrite it with a value that has no boundary, and the server
        // would reject a body that is perfectly well formed.
        ...(body ? { 'content-type': 'application/json' } : {}),
        // One slice of a file, sent as itself. Not multipart: a filename and a
        // mime type on each of a hundred pieces of one file is a hundred
        // chances to disagree about what is being uploaded, and the piece is
        // identified by its sequence number in the path.
        ...(raw ? { 'content-type': 'application/octet-stream' } : {}),
      },
      body: form ? form : raw ? raw : body ? JSON.stringify(body) : undefined,
      /*
       * `fresh` skips the BROWSER's copy, for a caller that has just changed the
       * thing it is asking about.
       *
       * The catalogue is cached for five minutes on purpose — it barely moves
       * and a seller flicking between printings should not re-fetch it. But an
       * operator who has just supplied a picture is inside that window by
       * definition: upload, reload, and the browser answers from the copy it
       * took BEFORE the upload. Reported on production, where the whole round
       * trip took under a minute: "I deployed to prod and did the same fix...
       * I don't see the same in prod." The server was right the entire time.
       */
      ...(fresh ? { cache: 'no-store' } : {}),
      signal,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    throw new ApiError('offline', 0);
  }

  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;
  let unreadable = false;
  try { payload = text ? JSON.parse(text) : null; } catch (e) { payload = null; unreadable = true; }

  if (!response.ok) {
    // Fastify's own schema failures arrive as `message`, ours as `error`.
    const code = (payload && (payload.error || payload.code)) || 'unknown';
    throw new ApiError(response.status === 429 ? 'rate_limited' : code, response.status, payload && payload.message, payload);
  }

  /*
   * A SUCCESS THAT PROMISED JSON AND DELIVERED NONE IS AN ERROR, NOT A NULL.
   *
   * This returned `payload` regardless, so an empty or mangled body on a 200
   * became `null` and the parse failure above was swallowed. Every caller then
   * reads a field off it — `r.listings`, `r.counts` — and the person sees
   * "Cannot read properties of null (reading 'listings')" in a toast: a message
   * that names the caller's variable and says nothing whatsoever about the
   * response that caused it. Reported from pre on 2026-10-01, where the server
   * had answered 200 with valid JSON to every request in its log, so the
   * evidence pointed at the one place the fault was not.
   *
   * ASKED OF THE CONTENT TYPE, because that is where the promise is made. A
   * response declaring `application/json` with nothing parseable in it has been
   * eaten by something between the server and here — a service worker serving a
   * stale empty entry, a truncated or mis-decoded body, a proxy. A 204 is
   * unaffected: it returns above, and "no content" is a real answer. So is a
   * genuinely bodiless 200 from an endpoint that never promised JSON.
   *
   * This does not fix such a response. It makes it say so.
   */
  const promisedJson = (response.headers.get('content-type') || '').includes('json');
  if (promisedJson && (unreadable || !text)) {
    throw new ApiError('unreadable', response.status);
  }
  return payload;
}

const qs = (params) => {
  const s = new URLSearchParams();
  // No params is a legitimate call, not a caller mistake: Object.entries(undefined)
  // throws "Cannot convert undefined or null to object", which surfaces as a blank
  // page on a cold listing rather than as anything about a query string.
  for (const [k, v] of Object.entries(params || {})) {
    /*
     * An array becomes a REPEATED KEY, not a comma-joined string.
     *
     * `?game=onepiece&game=pokemon` is what the API reads, and what its schema
     * describes. `set` would have produced `?game=onepiece,pokemon` — one value
     * that is not a game, rejected by the enum, so a browse filtered to two
     * games would 400 rather than return two games' cards.
     *
     * An empty array is nothing at all, which is right: an empty selection is
     * the absence of a filter everywhere on Browse, not a filter matching
     * nothing.
     */
    if (Array.isArray(v)) {
      for (const one of v) if (one !== undefined && one !== null && one !== '') s.append(k, String(one));
    } else if (v !== undefined && v !== null && v !== '') {
      s.set(k, String(v));
    }
  }
  const out = s.toString();
  return out ? `?${out}` : '';
};

/*
 * THE FOUR REQUESTS A SLICED UPLOAD IS MADE OF — module scope, not methods.
 *
 * They were methods on `api`, and every one of them was ALSO written out
 * inline inside `uploadInParts` just below. So each URL existed twice and the
 * named half had no callers, which `check:api-unused` reported as four
 * features the product does not have. It was reporting duplication rather than
 * absence — the more dangerous of the two, because two spellings of one URL
 * drift apart silently and the half that drifts is the one nobody reads. The
 * `mode` flag had already drifted: the inline copy sent it, the method did not.
 *
 * Module scope rather than methods is the honest shape, and it is the rule that
 * check reads: a method on `api` is something a SCREEN calls, and no screen
 * opens an upload — they call `uploadInParts`, which is the whole operation.
 * These are its steps.
 */
const openUpload = ({ kind, mode, filename, totalBytes }) =>
  request('/api/imports/uploads', { method: 'POST', body: { kind, mode, filename, totalBytes } });
const sendUploadPart = (id, seq, blob) =>
  request(`/api/imports/uploads/${encodeURIComponent(id)}/parts/${seq}`, { method: 'PUT', raw: blob });
const completeUpload = (id) =>
  request(`/api/imports/uploads/${encodeURIComponent(id)}/complete`, { method: 'POST' });
const cancelUpload = (id) =>
  request(`/api/imports/uploads/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const api = {
  /*
   * Exposed because two screens need it and neither owns the other: `browse`
   * reaches it through `listingCardProps` below, and the posting form's live
   * preview calls it directly. See its definition at the foot of this file.
   */
  pickWidth,
  setLocale,
  ApiError,


  // --- session ---
  /*
   * `referralCode` is optional and only ever arrives from a link somebody
   * followed. Left out of the body entirely when there is none — sending
   * `referralCode: null` would make every ordinary signup carry a field about a
   * feature it has nothing to do with.
   */
  signup: (email, password, displayName, referralCode) =>
    request('/api/auth/signup', {
      method: 'POST',
      body: referralCode ? { email, password, displayName, referralCode } : { email, password, displayName },
    }),
  login: (email, password) =>
    request('/api/auth/login', { method: 'POST', body: { email, password } }),

  /*
   * Asking for a password link, and spending one.
   *
   * `forgotPassword` ALWAYS resolves the same way whether or not the address
   * has an account — that is the server's decision, not a quirk to work around
   * here, and any code that tries to infer existence from this answer is
   * reading something that is deliberately constant.
   */
  forgotPassword: (email) =>
    request('/api/auth/password/forgot', { method: 'POST', body: { email } }),
  resetPassword: (token, password) =>
    request('/api/auth/password/reset', { method: 'POST', body: { token, password } }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  resendVerification: () => request('/api/auth/verify/resend', { method: 'POST' }),
  verifyEmail: (token) => request('/api/auth/verify', { method: 'POST', body: { token } }),

  // --- me ---
  me: () => request('/api/me'),
  updateMe: (patch) => request('/api/me', { method: 'PATCH', body: patch }),
  /** The whole account, for the trader's own use. Coarsening protects them from
   *  other traders, not from themselves — so this carries the precise point. */
  exportMe: () => request('/api/me/export'),
  /*
   * THE CONFIRMATION IS THE SERVER'S, NOT THE SCREEN'S.
   *
   * `POST /api/me/erase` requires `confirm: "ERASE"` and refuses anything else,
   * so this sent no body and would have answered 400 the first time anybody
   * pressed the button. Nobody ever did — it was defined here and called from
   * nowhere for as long as it has existed — which is exactly why the 400 was
   * never seen. See the note on the control in you.jsx.
   */
  eraseMe: () => request('/api/me/erase', { method: 'POST', body: { confirm: 'ERASE' } }),

  /*
   * The places you trade from.
   *
   * Every mutation answers with the WHOLE list AND the refreshed account, so
   * these all return the same shape. That is deliberate: `home_point` moves on
   * most of them, which makes the client's own coarsened point stale the moment
   * they succeed, and a list where one row was just deleted has had a promotion
   * rule applied to it that only the server knows. Patching either locally would
   * be a second implementation of both, free to disagree with the first.
   */
  locations: () => request('/api/me/locations'),
  addLocation: (body) => request('/api/me/locations', { method: 'POST', body }),
  updateLocation: (id, patch) =>
    request(`/api/me/locations/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
  removeLocation: (id) =>
    request(`/api/me/locations/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // --- listing photos ---
  /**
   * One file per request.
   *
   * Sent as FormData with NO content-type header set by hand: the browser has to
   * write it, because multipart needs a boundary parameter that only it knows.
   * Setting 'multipart/form-data' manually produces a body the server cannot
   * parse, and the error says nothing about the header.
   */
  addListingPhoto: (listingId, file) => {
    const body = new FormData();
    body.set('file', file);
    return request(`/api/listings/${listingId}/photos`, { method: 'POST', form: body });
  },
  removeListingPhoto: (listingId, photoId) =>
    request(`/api/listings/${listingId}/photos/${photoId}`, { method: 'DELETE' }),
  /**
   * A photograph, uploaded BEFORE the listing it belongs to.
   *
   * The listing is then created naming these ids, which is what makes "a listing
   * must have a picture" a rule at creation rather than a check afterwards.
   */
  uploadPhoto: (file) => {
    const body = new FormData();
    body.set('file', file);
    return request('/api/photos', { method: 'POST', form: body });
  },

  // --- importing an inventory ---
  /**
   * A Cardmarket stock export, uploaded whole.
   *
   * FormData for the same reason the photo endpoints use it — the browser has
   * to write the multipart boundary — and one file per request, because the
   * server reads exactly one and answers with a report about it.
   *
   * The answer is a REPORT, not a success: a 200 here can mean five hundred
   * listings, or none at all with five hundred reasons. Every code it can
   * refuse the whole upload with (`not_a_stock_export`, `file_too_large`)
   * carries a number or a list alongside it, which is why
   * `ApiError.payload` exists and why import.jsx reads it rather than the
   * one-sentence table above.
   */
  /*
   * The same call twice: once to look, once to do it.
   *
   * `dryRun` runs the whole import server-side and rolls it back, so what comes
   * back is not a forecast of the import — it is the import, measured and undone.
   * That is why the file is sent again rather than a token being kept: there is
   * no half-created state on the server to refer back to, which is the entire
   * point of a preview that leaves nothing behind.
   *
   * In the query string rather than the form, because the API's rate limiter has
   * to know which of its two allowances the call belongs to before it has read
   * a multipart body.
   */
  importCardmarket: (file, options) => {
    const body = new FormData();
    body.set('file', file);
    /*
     * Both flags in the query string, because the server reads them before the
     * multipart body exists — see the rate limiter's hook. `sync` is the shop's
     * mode: the file is the inventory, so live articles take its prices and
     * anything missing from it comes down.
     */
    const flags = [
      options && options.dryRun ? 'dryRun=1' : '',
      options && options.mode === 'sync' ? 'mode=sync' : '',
    ].filter(Boolean).join('&');
    return request(`/api/imports/cardmarket${flags ? `?${flags}` : ''}`, { method: 'POST', form: body });
  },

  /*
   * The OTHER export the same extension writes: the wants lists.
   *
   * Its own endpoint rather than a flag, because almost nothing is shared past
   * the upload — different columns, a different table, a different set of
   * reasons a row can fail. The preview rule is identical, and so is the reason
   * the file is sent twice rather than a token kept: a preview leaves nothing
   * behind to refer back to.
   */
  importCardmarketWants: (file, options) => {
    const body = new FormData();
    body.set('file', file);
    const dryRun = options && options.dryRun ? '?dryRun=1' : '';
    return request(`/api/imports/cardmarket-wants${dryRun}`, { method: 'POST', form: body });
  },

  /*
   * What became of an import too big to finish inside its request.
   *
   * Polled rather than pushed, because the answer is wanted by exactly one
   * screen belonging to exactly one person, and a websocket for that is
   * machinery nobody needs. It is also readable later: a background import is
   * precisely the case where somebody closes the tab.
   */
  importJob: (id) => request(`/api/imports/jobs/${encodeURIComponent(id)}`),
  /*
   * The import already in flight, if there is one. Asked when the screen opens,
   * because the job id used to live only in this screen's state — so leaving
   * the page lost an import the server was still reading.
   */
  currentImportJob: () => request('/api/imports/jobs'),
  /*
   * A FILE TOO BIG FOR ONE REQUEST, sent in pieces.
   *
   * Cloudflare refuses a body at 100 MB — measured, not assumed: 95 MB reaches
   * the API and 100 MB comes back as Cloudflare's own 413, which no cap of ours
   * can change. Traefik cuts a request at about sixty seconds on top of that.
   * So a large upload is many small requests, and the server reassembles them
   * from the sequence numbers.
   *
   * Every piece is retried a few times before the upload is given up on. A
   * dropped packet in the middle of half a gigabyte used to mean starting
   * again; now it means sending five megabytes again.
   */
  /**
   * The whole of it: open, send every piece, hand it over.
   *
   * `onProgress` is called with bytes sent so far, which is the first time this
   * product has been able to say anything true about an upload in flight —
   * "reading your file" used to cover an upload nobody could see inside, which
   * is how a stalled 444 MB transfer looked exactly like a working one.
   *
   * A failure anywhere drops the upload rather than leaving it half sent. The
   * sweep would have taken it in six hours; not leaving half a gigabyte lying
   * around for six hours is better.
   */
  async uploadInParts({ file, kind, mode, partBytes, onProgress, attempts = 3 }) {
    const opened = await openUpload({ kind, mode, filename: file.name, totalBytes: file.size });
    const size = opened.partBytes || partBytes;
    try {
      let sent = 0;
      for (let seq = 0; sent < file.size; seq += 1) {
        const end = Math.min(sent + size, file.size);
        const slice = file.slice(sent, end);
        let lastError = null;
        for (let go = 0; go < attempts; go += 1) {
          try {
            await sendUploadPart(opened.uploadId, seq, slice);
            lastError = null;
            break;
          } catch (e) {
            lastError = e;
            // A moment between goes, growing: an overloaded server or a phone
            // changing cell is not helped by three immediate retries.
            await new Promise((r) => setTimeout(r, 400 * (go + 1)));
          }
        }
        if (lastError) throw lastError;
        sent = end;
        if (onProgress) onProgress(sent, file.size);
      }
      return await completeUpload(opened.uploadId);
    } catch (e) {
      await cancelUpload(opened.uploadId).catch(() => {});
      throw e;
    }
  },
  /*
   * "I have seen how it went", sent once the finished result is on screen.
   *
   * Without it the recovery above kept handing back yesterday's result on every
   * visit until its day ran out, which is the report: "the information about the
   * file that has been imported is stale. I already saw it before."
   */
  markImportJobSeen: (id) =>
    request(`/api/imports/jobs/${encodeURIComponent(id)}/seen`, { method: 'POST' }),

  /*
   * The thresholds, asked once when the import screen opens.
   *
   * The screen needs the background line BEFORE it sends anything: a file over
   * it gets no preview, and deciding by trying would mean uploading the whole
   * thing to be told it was too big.
   */
  importLimits: () => request('/api/imports/limits'),

  // --- telling us something is broken ---
  /** No session needed: the report that matters most is often from somebody who
   *  could not get one. */
  sendFeedback: (body) => request('/api/feedback', { method: 'POST', body }),

  /*
   * "That is not this card."
   *
   * About OUR catalogue picture, never about the listing or its seller — which is
   * why it takes a card code and an artwork rather than a listing id. One wrong
   * picture is one fault however many people are selling that card.
   */
  reportCardImage: (body) => request('/api/catalog/image-reports', { method: 'POST', body }),

  /*
   * The operator's queue, and the one decision that empties it.
   *
   * Both answer 404 rather than 403 to anybody who is not an operator, so a
   * client cannot use their failure to discover that the routes exist. Nothing
   * here retries or reports differently on 404 for that reason.
   */

  /*
   * DRAWS, for the operator screen — migration 107.
   *
   * Three calls and no delete. A campaign with entries in it is a record of
   * what people were told and what they earned, and the way to stop one is to
   * end it, which is what `status: 'ended'` is for. Removing the row would take
   * everybody's entries with it by cascade, which is not a thing a button
   * should be able to do.
   */
  /*
   * Your code, the people who used it, and the draws.
   *
   * Two calls rather than one, because they have different readers: a guest on
   * an invite link may read the draws and has no code of their own.
   */
  myReferrals: (signal) => request('/api/referrals/mine', { signal }),
  campaigns: (signal) => request('/api/campaigns', { signal }),
  /* "I have watched my result." Written after the reveal plays, never before. */
  markRevealed: (id) => request(`/api/campaigns/${encodeURIComponent(id)}/revealed`, { method: 'POST' }),
  /* Which of the offered prizes this winner wants. Changeable until handed over. */
  chooseCampaignPrize: (id, prizeId) =>
    request(`/api/campaigns/${encodeURIComponent(id)}/prize`, { method: 'POST', body: { prizeId } }),
  // Whose code is this? Answers with a name or with nothing, never with a reason.
  inviter: (code) => request(`/api/referrals/${encodeURIComponent(code)}`),

  /*
   * An operator correcting a listing's printing, from the listing page. The
   * server takes the title and set from the catalogue, so this sends only the
   * printing, the language and the finish.
   */
  adminFixListingPrinting: (id, { sourceProductId, language, foil }) =>
    request(`/api/admin/listings/${encodeURIComponent(id)}/printing`, {
      method: 'POST', body: { sourceProductId, language, foil },
    }),
  adminCampaigns: () => request('/api/admin/campaigns'),
  adminCreateCampaign: (campaign) => request('/api/admin/campaigns', { method: 'POST', body: campaign }),
  adminUpdateCampaign: (id, patch) =>
    request(`/api/admin/campaigns/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
  /*
   * Who won, and marking that they have it. Operator only: this one carries
   * pickup codes, claims and choices. The NAMES are public since the owner's
   * ask on Discord, 2026-11-02 — "Let's work on showing in a campaign all the
   * winners' nicknames. But important, it should not spoil it for winners
   * that haven't do their draw." — and arrive as `winnersList` on
   * `campaigns()`, filled in only where that winner has watched.
   */
  /*
   * A campaign's words per language. Unresolved on purpose: an operator is
   * deciding WHICH languages exist, and a fallback would hide whether the
   * English is written yet.
   */
  /* The options a winner chooses between. Read and written as one list. */
  adminCampaignPrizes: (id) => request(`/api/admin/campaigns/${encodeURIComponent(id)}/prizes`),
  adminSaveCampaignPrizes: (id, prizes) =>
    request(`/api/admin/campaigns/${encodeURIComponent(id)}/prizes`, { method: 'PUT', body: { prizes } }),

  adminCampaignCopy: (id) => request(`/api/admin/campaigns/${encodeURIComponent(id)}/copy`),
  adminSaveCampaignCopy: (id, locale, copy) =>
    request(`/api/admin/campaigns/${encodeURIComponent(id)}/copy/${encodeURIComponent(locale)}`,
            { method: 'PUT', body: copy }),
  /* One language's own creative, for a campaign whose picture has words in it. */
  adminCampaignCopyImage: (id, locale, file) => {
    const form = new FormData();
    form.append('file', file);
    return request(`/api/admin/campaigns/${encodeURIComponent(id)}/copy/${encodeURIComponent(locale)}/image`,
                   { method: 'POST', form });
  },
  adminRemoveCampaignCopyImage: (id, locale) =>
    request(`/api/admin/campaigns/${encodeURIComponent(id)}/copy/${encodeURIComponent(locale)}/image`,
            { method: 'DELETE' }),

  adminCampaignWinners: (id) => request(`/api/admin/campaigns/${encodeURIComponent(id)}/winners`),
  /*
   * A prize picture. `form` rather than `body`: it is a file, and it goes the
   * same way a seller's photograph does.
   */
  adminCampaignImage: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return request(`/api/admin/campaigns/${encodeURIComponent(id)}/image`, { method: 'POST', form });
  },
  adminRemoveCampaignImage: (id) =>
    request(`/api/admin/campaigns/${encodeURIComponent(id)}/image`, { method: 'DELETE' }),
  adminMarkClaimed: (id, traderId, claimed) =>
    request(`/api/admin/campaigns/${encodeURIComponent(id)}/claimed`, { method: 'POST', body: { traderId, claimed } }),

  adminImageReports: () => request('/api/admin/image-reports'),
  /* Deliberately not live — see the route. `refreshedAt` comes back with it. */
  adminOverview: (days) => request(`/api/admin/overview${days ? `?days=${days}` : ''}`),
  /* Rebuilds the materialised view behind the overview. Slow by nature — it is
     a point-in-polygon per listing and per trade — so the screen disables its
     button rather than letting somebody queue three of them. */
  adminRefreshOverview: () => request('/api/admin/overview/refresh', { method: 'POST' }),
  /* Where the traffic came from. Signed-in only rather than operator-only on the
     server, but it is reached from the back office because that is who asks. */
  eventsSummary: (days) => request(`/api/events/summary${days ? `?days=${days}` : ''}`),
  adminDecideImage: (body) => request('/api/admin/image-reports/decide', { method: 'POST', body }),

  // --- catalogue ---
  /** Grouped: one entry per card, its printings underneath. */
  /** Flat: one entry per product. For picking an exact printing to list. */
  /** Typeahead. Takes a signal, because every keystroke supersedes the last. */
  searchSuggestions: (params, signal) => request(`/api/catalog/suggest${qs(params)}`, { signal }),
  /*
   * Every printing of one card, for choosing WHICH ONE you own.
   *
   * Fetched once, when a card is picked, rather than ridden along with the
   * suggestions: eight suggestions carrying every printing each would be a few
   * hundred rows per keystroke, of which all but one card's are thrown away.
   *
   * Takes a signal for the same reason the typeahead does — picking a second card
   * before the first one's printings land must not leave the wrong list on screen.
   */
  cardPrintings: (params, signal, fresh) =>
    request(`/api/catalog/printings${qs(params)}`, { signal, fresh }),
  /*
   * What Cardmarket says a printing goes for, so the posting form can show the
   * price a listing will be born at before it exists.
   *
   * `foil` matters as much as the product: a foil is priced separately, and
   * ticking the box changes which number applies without changing the card.
   */
  catalogPrice: (params, signal) => request(`/api/catalog/price${qs(params)}`, { signal }),
  /*
   * Market-following across an inventory: a selection, or all of it.
   *
   * `all` travels as a flag rather than as a list this client assembles. With
   * three hundred listings the browser would otherwise send three hundred ids
   * — a body that grows with the inventory and is already stale if a card was
   * posted while the page was open. The server knows what "all of mine" means
   * at the instant it writes.
   */
  setFollowingInBulk: (body) => request('/api/listings/following', { method: 'POST', body }),

  /*
   * A SHOP'S OWN CARDTRADER ACCOUNT.
   *
   * The token goes UP and never comes back down: the only thing these answers
   * carry is a tail, enough to recognise which credential is stored. Four calls
   * rather than one with a mode, because they are four different acts — and
   * `connectCardtrader` is the only one that ever carries a secret, which is
   * worth being able to see at a glance when reading a network log.
   */
  cardtrader: () => request('/api/shop/cardtrader'),
  connectCardtrader: (token) => request('/api/shop/cardtrader', { method: 'PUT', body: { token } }),
  /*
   * WRITE-BACK (ADR 0011): the switch, and where it stands. Both answer counts
   * — copies switching on would take off, copies off CardTrader because of
   * trades here — and the panel words them in its confirmations.
   */
  cardtraderWriteBack: () => request('/api/shop/cardtrader/write-back'),
  setCardtraderWrites: (writesEnabled) =>
    request('/api/shop/cardtrader', { method: 'PATCH', body: { writesEnabled } }),
  disconnectCardtrader: () => request('/api/shop/cardtrader', { method: 'DELETE' }),
  /*
   * Lending us the CardTrader webhook, and taking it back.
   *
   * `restoreUrl` is what the SHOP says they had, because CardTrader will not
   * tell us: `GET /info` does not carry the field and `GET /app` is a 404, so
   * the only read is the write that already overwrote it.
   */
  /*
   * The address a shop pastes into THEIR OWN CardTrader settings. We never set
   * it for them: their token is for reading their stock, and quietly editing
   * their account settings with it would be an unmentioned second exception to
   * that.
   */
  cardtraderWebhookUrl: () => request('/api/shop/cardtrader/webhook', { method: 'POST' }),
  /*
   * No `stopCardtraderWebhook`. Stopping is removing the address in their own
   * CardTrader settings — one place, theirs — and disconnecting the account
   * takes the slug with it. A button that only made US stop listening left
   * CardTrader posting into silence with nothing saying so.
   */
  /**
   * What the seller decided about a scan.
   *
   * Fire and forget, deliberately: the caller is on its way to the posting form
   * and a corpus must never be in that doorway. The server answers 204 to
   * everything, including a token it does not recognise.
   */
  /**
   * "The set I am holding is not in your list."
   *
   * A report rather than a field on the listing: a typed set matches no filter
   * and reconciles with no import, and the gap it describes is invisible to us.
   * This lands in a queue somebody can act on.
   */
  reportMissingSet: (saidSet, productId, game) =>
    request('/api/catalog/set-reports', {
      method: 'POST',
      body: { saidSet, ...(productId ? { productId } : {}), ...(game ? { game } : {}) },
    }),

  // --- listings ---
  listings: (params, signal) => request(`/api/listings${qs(params)}`, { signal }),
  // Before the :id route, or "mine" would be read as a listing id.
  myListings: (params, signal) => request(`/api/listings/mine${qs(params || {})}`, { signal }),
  listing: (id, params) => request(`/api/listings/${encodeURIComponent(id)}${qs(params)}`),
  /*
   * A trader's page and their shelf, keyed by handle OR id — whichever was in
   * the address. Two requests rather than one payload: the header is small and
   * arrives first, while the cards page like every other list on the site.
   */
  /*
   * Verified game shops near you, for meeting in. Public, because everything it
   * returns is a business address that is already on the shop's own door.
   */
  nearbyShops: (params) => request(`/api/shops/nearby${qs(params || {})}`),

  /*
   * A SHOP OF YOUR OWN — the whole flow was built on the server and never wired
   * up here, which is why production holds zero of them. Apply, read back the
   * state, withdraw; and for an operator, the queue and the decision.
   *
   * `applyShop` is a PUT and answers both cases deliberately: a rejection that
   * cannot be answered is a dead end, and the commonest rejection is a wrong
   * address. Editing a verified shop sends it back to the queue, because the
   * tick says somebody checked THAT address.
   */
  myShop: () => request('/api/shop'),
  applyShop: (body) => request('/api/shop', { method: 'PUT', body }),
  /*
   * The handle a name WOULD get — the shop's page address — asked while the
   * name is typed, so the form shows it before anything is saved. A forecast:
   * send it back as `shownHandle` and the save says (`handleMoved`) if a race
   * took it in between.
   */
  shopHandle: (name) => request(`/api/shop/handle${qs({ name })}`),
  closeShop: () => request('/api/shop', { method: 'DELETE' }),
  /*
   * PUTTING ON THE SHOP. One column on the session, and every other route in
   * this file starts answering as the shop — which is why there is nothing here
   * for "the shop's listings" or "the shop's trades": they are `myCards` and
   * `trades`, asked while acting as it.
   *
   * `null` takes it off again.
   */
  actAsShop: (shopId) => request('/api/shop/act', { method: 'POST', body: { shopId: shopId ?? null } }),
  /*
   * The one thing the dashboard knows that no other screen does. Narrowed to
   * the games in scope the way `myListings` is — `game`, repeated — so Today's
   * figures and the Stock table are answers to the same question.
   */
  shopDemand: (games) => request(`/api/shop/demand${qs({ game: games })}`),
  /*
   * Many prices at once. `all` is a flag the server resolves as it writes,
   * never a list the browser assembles — the same shape as the following
   * switch, and for the reason written beside it there.
   */
  bulkPrice: (body) => request('/api/listings/price', { method: 'POST', body }),
  adminShops: () => request('/api/admin/shops'),
  adminDecideShop: (body) => request('/api/admin/shops/decide', { method: 'POST', body }),
  /*
   * An operator supplying a picture the catalogue has none of. Multipart, like
   * every other upload here, because it carries a file — `request` sets no
   * content-type for a FormData body, so the browser writes the boundary.
   */
  adminUploadCardImage: (sourceProductId, file) => {
    const form = new FormData();
    form.set('sourceProductId', String(sourceProductId));
    form.set('file', file, file.name || 'card.jpg');
    // `form`, not `body` — see `request`: a `body` would get a JSON
    // content-type stamped on it and the multipart boundary would be lost.
    return request('/api/admin/card-images', { method: 'POST', form });
  },
  adminRemoveCardImage: (sourceProductId) =>
    request(`/api/admin/card-images/${encodeURIComponent(sourceProductId)}`, { method: 'DELETE' }),
  /** Sets sellers say we are missing, grouped by the set rather than the report. */
  adminSetReports: () => request('/api/admin/set-reports'),
  /**
   * One gap, closed once, either way.
   *
   * `resolution` is 'added' where the catalogue was short, 'dismissed' where it
   * was not. Both close every open report naming the set; they mean opposite
   * things and the server refuses to guess between them.
   */
  adminHandleSetReport: (saidSet, game, resolution) =>
    request('/api/admin/set-reports/handled',
      { method: 'POST', body: { saidSet, game: game ?? null, resolution } }),
  trader: (key, params) => request(`/api/traders/${encodeURIComponent(key)}${qs(params || {})}`),
  traderListings: (key, params) => request(`/api/traders/${encodeURIComponent(key)}/listings${qs(params || {})}`),
  /*
   * How to reach one seller. Its own request on purpose: browse is public and
   * hands out sixty listings at a time, so a contact returned there would be a
   * phone number published to anybody with a URL. Answers 401 for a guest, which
   * is what the sign-in prompt is triggered by.
   */
  listingContact: (id) => request(`/api/listings/${encodeURIComponent(id)}/contact`),
  createListing: (listing) => request('/api/listings', { method: 'POST', body: listing }),
  /*
   * Notes and stock. The price and the grade are terms somebody agreed to and
   * stay uneditable — see the route for why, and for why how many copies are
   * left is a different kind of fact.
   *
   * One method taking a patch rather than two taking a field each: the endpoint
   * accepts either or both, and two methods would invite a screen that edits
   * both to send two requests, of which the second can fail on its own.
   */
  editListing: (id, patch) => request(`/api/listings/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
  withdrawListing: (id) => request(`/api/listings/${encodeURIComponent(id)}/withdraw`, { method: 'POST' }),
  /*
   * Several at once, as a list of ids rather than as a flag — the same shape and
   * the same reasoning as `removeWants`. "All my listings" is what the price
   * control sends, because following a price is additive and reversible; taking
   * an inventory off the market is neither, so the scope stays something
   * somebody could see on screen until that decision is made.
   *
   * The answer counts what was really withdrawn, which can be lower than the
   * list: a listing taken down in another tab is not an error.
   */
  withdrawListings: (ids) => request('/api/listings/withdraw', { method: 'POST', body: { ids } }),

  /**
   * Push. The key is fetched rather than built in, so rotating the pair is one
   * environment variable rather than a deploy of the client — and the two halves
   * cannot disagree about which key a subscription was made with.
   */
  pushKey: () => request('/api/push/key'),
  pushSubscribe: (subscription) => request('/api/push/subscribe', { method: 'POST', body: subscription }),
  pushUnsubscribe: (endpoint) => request('/api/push/unsubscribe', { method: 'POST', body: { endpoint } }),
  pushDevices: () => request('/api/push/devices'),
  // The Android app handing over its Firebase token, which is the same act as a
  // browser subscribing: it turns a device we know about into one we can reach.
  pushDevice: (body) => request('/api/push/device', { method: 'POST', body }),
  /*
   * Makes this account's devices buzz, and reports the push service's verdict
   * per device. A diagnostic, rate limited to five an hour on the server, and
   * deliberately never described as "delivered": accepted by Google is all
   * anybody can know from here, and the gap between the two is the reported bug.
   */
  pushTest: () => request('/api/push/test', { method: 'POST', body: {} }),
  // Drops one device from the list. Not an unsubscribe — a browser's
  // subscription lives in that browser — but it is what somebody means when they
  // take a device off: we stop being able to reach it.
  pushForgetDevice: (id) => request(`/api/push/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * Places to meet. Proxied through our API, so the geocoder sees this server
   * rather than a trader's IP and the list of cafés they were considering.
   */
  searchPlaces: (params, signal) => request(`/api/places${qs(params)}`, { signal }),

  // --- conversations ---
  conversations: (signal) => request('/api/conversations', { signal }),
  /** Opening a conversation marks it read — that is what opening one means. */
  conversation: (id) => request(`/api/conversations/${encodeURIComponent(id)}`),
  // `noCard`: the composer's ×, "send only the link, without the card".
  sendMessage: (toTraderId, body, noCard = false) => request('/api/conversations', {
    method: 'POST', body: { toTraderId, body, ...(noCard ? { noCard: true } : {}) },
  }),

  // --- trades ---
  trades: (params = {}, signal) => request(`/api/trades${qs(params)}`, { signal }),
  /** Finished trades a page at a time: `{ trades, next }`, `before` being the last page's `next`. */
  pastTrades: (params = {}, signal) => request(`/api/trades${qs({ past: true, ...params })}`, { signal }),
  /** One trade of yours by id, whatever scope or page the list is on. 404 when it is not yours. */
  trade: (id) => request(`/api/trades/${encodeURIComponent(id)}`),
  proposeTrade: (body) => request('/api/trades', { method: 'POST', body }),
  reportTrade: (id, reason, note) =>
    request(`/api/trades/${encodeURIComponent(id)}/report`, { method: 'POST', body: { reason, ...(note ? { note } : {}) } }),
  tradeAction: (id, action, body) =>
    request(`/api/trades/${encodeURIComponent(id)}/${action}`, { method: 'POST', body: body || {} }),

  // --- wants ---
  /**
   * The two numbers a badge needs, without the two lists it does not.
   *
   * What every open tab asks for, every few seconds. Fetching the whole
   * conversation and trade lists to count them was the dominant load on the
   * system, and everything but the counts was discarded.
   */
  pulse: (signal) => request('/api/pulse', { signal }),
  wants: (params, signal) => request(`/api/wants${qs(params || {})}`, { signal }),
  addWant: (want) => request('/api/wants', { method: 'POST', body: want }),
  removeWant: (id) => request(`/api/wants/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /*
   * Several at once, as a list of ids rather than as a flag.
   *
   * Unlike market-following, there is no "all my wants" here — and that is the
   * open question rather than an oversight. Following a price is additive and a
   * wrong "all" is one more bulk edit away from being undone; removing is not,
   * so the scope stays a list somebody could see on screen until the product
   * decision is made.
   *
   * The answer is a count of what was really deleted, which can be lower than
   * the list: a want removed in another tab is not an error.
   */
  removeWants: (ids) => request('/api/wants/remove', { method: 'POST', body: { ids } }),
};

/**
 * A set's name as this reader's interface shows it: the Spanish name to a
 * Spanish interface where the API sent one (`setNameEs`, migration 157), the
 * English `setName` otherwise. The one place that choice is made: the listing
 * view's `set` asks it, and so do the screens that read a raw API row.
 */
export function setNameFor(row, loc = locale) {
  return (loc === 'es-ES' && row.setNameEs) || row.setName || undefined;
}

/**
 * The same choice for a raw catalogue row — a printing from
 * /api/catalog/printings or a suggestion — which names its set `expansion`
 * and sends the Spanish one as `expansionEs`.
 *
 * A LABEL, and only that. The English `expansion` stays what a picker groups
 * and dedupes by, what "(Non-English)" is read from, and what a listing stores:
 * this is called where a set name is drawn and nowhere else.
 */
export function expansionNameFor(row, loc = locale) {
  return (loc === 'es-ES' && row.expansionEs) || row.expansion || undefined;
}

/**
 * The name a suggestion from /api/catalog/suggest is drawn and picked under.
 *
 * The name it was found by where one was typed (`shownName`: "Elfos de
 * Llanowar" typed, "Elfos de Llanowar" shown). Found by what is printed on the
 * card ("DOM 168"), there is no name typed to follow, so the READER's language
 * decides — the owner, 2026-10-23: "prioritize the language of the client and
 * english as the ultimate fallback": the Spanish name (`nameEs`) to a Spanish
 * interface where we hold one, English otherwise.
 *
 * A LABEL. `name` stays the English one every caller stores and searches by.
 */
export function suggestionNameFor(item, loc = locale) {
  return item.shownName || (loc === 'es-ES' && item.nameEs) || item.name;
}

/**
 * The API speaks in values; the screens speak in display shapes. This is the one
 * place that translates, so no screen has to know that a distance arrives as
 * `{ metres, known }`.
 *
 * Money is the exception, deliberately: it stays in integer cents all the way to
 * the render, under a name that says so. See `priceCents` below.
 *
 * Distance keeps all three of the API's states rather than collapsing them:
 *   number    — at or above the 1 km floor
 *   null      — known, but below it
 *   undefined — not known at all, because the viewer has set no location
 * Collapsing the last two would turn "we don't know" into "very close", which is a
 * lie the trader would act on.
 */
function toListingView(l) {
  return {
    id: l.id,
    // The card behind the listing. A trade offers cards, not listings.
    cardId: l.cardId,
    // Their shop, where they have a verified one — so proposing a trade to a
    // game shop can settle the meeting place in one tap.
    sellerShop: (l.seller && l.seller.shop) || null,
    // In the card's own language where the catalogue holds its name, English
    // otherwise. Decided by the server; nothing here to choose.
    title: l.title,
    /*
     * THE SET IN SPANISH WHERE WE HOLD ITS NAME, for a Spanish interface;
     * English otherwise (migration 157). The API sends both, as values, and the
     * reader's language picks — so this is a getter on the CURRENT locale rather
     * than a choice frozen when the response arrived: switching language in the
     * header renames the sets already on screen, and a response that landed
     * before the stored locale was applied is not stuck in the wrong one.
     */
    setName: l.setName || undefined,
    setNameEs: l.setNameEs || undefined,
    get set() { return setNameFor(this); },
    game: l.game,
    condition: l.grade,
    // The language the CARD is printed in — not the interface's. Always sent
    // since migration 050; the `|| undefined` is what stops an unexpected null
    // from becoming an empty chip on the listing page.
    language: l.language || undefined,
    /*
     * Whether it is a foil, as a real boolean.
     *
     * `!!` rather than the `|| undefined` above it, and the difference is the
     * whole of migration 058: an absent language means the server sent nothing
     * and a chip reading "unknown" would be a broken field, while an absent
     * foil means false. A listing from before the column existed, and any
     * response this client is handed by an older build, reads as not foil —
     * which is what it is.
     */
    foil: !!l.foil,
    mode: l.mode,
    /*
     * Cents, carried through untouched, and named so.
     *
     * This was `l.priceCents / 100`, which produced a euro FLOAT called `price`
     * — and then every screen rendered it raw, so €12.00 read as "12" and €12.50
     * as "12.5" with the symbol glued on. The fix is to format with Intl, and the
     * one formatter we have (`formatCents`) takes CENTS. A `price` in euros
     * sitting next to it is a 100× error waiting to be written by somebody who
     * reads the call and not the divisor: `formatCents(listing.price)` renders
     * €12.50 as "0,13 €", and it looks entirely plausible on the way past.
     *
     * So the euro float stops existing. The unit is in the NAME at every call
     * site, money never touches a float, and the only place that turns cents into
     * something a person reads is `formatCents`.
     */
    priceCents: l.priceCents == null ? undefined : l.priceCents,
    note: l.note || undefined,
    km: !l.distance.known ? undefined : l.distance.metres == null ? null : l.distance.metres / 1000,
    status: l.status,
    // The publisher's picture where the catalogue has one, falling back on the
    // server to the trader's own first photo — three of the five games have no
    // catalogue artwork at all.
    //
    // Which picture that is, is settled server-side and in one place: the artwork
    // its seller said they hold, else the catalogue's. Resolving it again here
    // would be a second rule that can disagree with the first.
    /*
     * WHICH CARD THIS IS, in the catalogue's own words.
     *
     * Carried because the listing page offers "this is not the right picture",
     * and that report is about the CARD rather than the listing -- one wrong
     * catalogue picture is wrong on every listing of that card at once.
     *
     * Dropping it here was a real bug for the length of one deploy: the server
     * sent `cardCode`, this mapping did not copy it, so the control that needs
     * it rendered nothing at all. Nothing failed; the button was simply never
     * there, which is the failure mode a mapping like this has -- the screen
     * cannot tell a field that is absent from a field that is null.
     */
    cardCode: l.cardCode || undefined,
    /*
     * The catalogue printing this listing points at, and the seller's own rule
     * for following its market price.
     *
     * CARRIED HERE OR THE CONTROL NEVER RENDERS — which is the bug the note
     * above `cardCode` describes, and it cost a deploy the last time. The edit
     * dialog decides whether to offer market-following on `sourceProductId`
     * being present, so dropping it here would hide the feature completely
     * while every test of the server kept passing.
     *
     * `pricing` comes back only for the listing's own seller, so it is
     * undefined on everybody else's listings — which is exactly what the
     * dialog needs to know, and why it is copied straight across rather than
     * defaulted to an empty object here.
     */
    sourceProductId: l.sourceProductId == null ? undefined : l.sourceProductId,
    pricing: l.pricing,
    /* Which drawing is on screen, so a report names the picture and not just the
       card. Undefined where the seller never chose one. */
    image: l.imageUrl || undefined,
    /*
     * The same picture's smaller copies, as a `srcset`, or null.
     *
     * `image` ABOVE STAYS THE ORIGINAL, and shrinking it here was a real bug for
     * the length of one commit: the listing page's lightbox magnifies to one
     * image pixel per screen pixel and reads `image` through the gallery's
     * `full`, so a view whose `image` was a 480-wide copy would have made the
     * one screen that exists for detail the only screen showing less of it.
     *
     * The choice is made per screen instead. `listingCardProps` picks a width
     * because `ListingCard` takes a `src` and has no `srcset`; the listing
     * gallery hands this whole string to its own `<img>` and lets the browser
     * pick; the lightbox is given neither and keeps the original.
     */
    imageSrcset: l.imageSrcset || null,
    /*
     * The trader's own photographs of THIS copy.
     *
     * Always an array, never undefined: a screen that maps over it should not
     * have to ask first, and browse deliberately sends an empty one rather than
     * omitting the field, so "no photos" and "not loaded here" look the same to
     * every renderer — which is correct, because neither should render a gallery.
     */
    photos: l.photos || [],
    /*
     * Copies: how many, how many promised, how many still going.
     *
     * Defaulted rather than passed through, because a screen asking "how many
     * are left" must get a number: `undefined` in a quantity picker's maximum
     * silently means "no limit", which is the wrong way for a missing field to
     * fail. One copy, none committed, is what every listing was before stock
     * existed and the safest thing an absent field can mean.
     */
    stock: l.stock || { quantity: 1, committed: 0, available: 1 },
    seller: l.seller,
    // Earned and mutually confirmed; the flag is set only when reports are out of
    // proportion to trades. See api/src/lib/reputation.ts.
    sellerHandle: l.seller ? l.seller.handle : null,
    sellerTrades: l.seller ? l.seller.completedTrades : 0,
    sellerFlagged: l.seller ? l.seller.flagged : false,
    createdAt: l.createdAt,
    /*
     * How many traders have this card on a wants list. Your own shelf only —
     * the server sends it for `/mine` and nowhere else, so it is undefined on a
     * browse row rather than nought. Mapped here because a field this file does
     * not name never reaches a screen, however correct the server is.
     */
    wantedBy: l.wantedBy,
  };
}

/**
 * A trade, shaped for the MeetupCard the design system already has.
 *
 * The API's vocabulary is the state machine's (proposed / accepted / declined /
 * cancelled / completed); the card's is the reader's (awaiting reply, confirmed,
 * done). Mapping here rather than renaming either side keeps the state machine
 * precise and the label human.
 */
const TRADE_STATE = {
  proposed: 'proposed',
  accepted: 'confirmed',
  completed: 'done',
  declined: 'cancelled',
  cancelled: 'cancelled',
};

function toTradeView(t) {
  return {
    id: t.id,
    status: t.status,
    cardStatus: TRADE_STATE[t.status] || 'proposed',
    role: t.role,
    them: t.them,
    listingId: t.listingId,
    // How many copies. Defaulted to one for the same reason as a listing's
    // stock: a screen that reads this needs a number, not a maybe.
    quantity: t.quantity == null ? 1 : t.quantity,
    can: t.can,
    youReported: t.youReported,
    // When it was proposed, so the conversation can put it where it happened
    // rather than always at the end. See inbox.jsx.
    createdAt: t.createdAt,
    completion: t.completion,
    cancelReason: t.cancelReason,
    /*
     * Where the copies stand on CardTrader — sent only to the shop whose stock
     * it is (ADR 0011). Carried as the server's code and counts; `CardTraderLine`
     * words it. A mapper that dropped it would leave every API test green.
     */
    cardtrader: t.cardtrader || null,
    place: t.meetup ? t.meetup.placeName : null,
    address: t.meetup ? t.meetup.placeNote : null,
    // A link to open the meeting point in whatever maps app the phone uses.
    directions: t.meetup ? mapsLink({
      name: t.meetup.placeName,
      lat: t.meetup.point ? t.meetup.point.lat : null,
      lng: t.meetup.point ? t.meetup.point.lng : null,
    }) : null,
    when: t.meetup && t.meetup.at ? formatMeetAt(t.meetup.at) : null,
    /*
     * The instant itself, beside the sentence made from it.
     *
     * `when` above is already formatted for a reader, and a field that has to be
     * PREFILLED with it — the accept dialog's `datetime-local` — needs a machine
     * value. Parsing the pretty string back would be a second implementation of
     * a format that exists in exactly one place, and the two would come apart on
     * the first day somebody changed the separator or the weekday.
     *
     * That absence is what made accepting a proposal as offered impossible: the
     * dialog could not prefill a time it had only ever been handed as prose, so
     * its confirm button stayed disabled until the recipient retyped a time the
     * proposer had already chosen.
     */
    meetAt: t.meetup ? t.meetup.at : null,
    /*
     * AT A SHOP'S COUNTER, which means there is no hour and none is missing.
     * Every screen that writes "time not agreed yet" has to ask this first, or
     * a finished arrangement reads as an unfinished one.
     */
    atCounter: Boolean(t.meetup && t.meetup.atCounter),
    // At YOUR counter: the shop's own side, which needs no directions to itself.
    hostedByYou: Boolean(t.meetup && t.meetup.hostedByYou),
    /*
     * The agreed pin as [lat, lng], for anything that has to DRAW it.
     *
     * `directions` above is the same fact turned into a maps URL, which is all a
     * "take me there" button needs and no use whatever to a map.
     */
    placePoint: t.meetup && t.meetup.point ? [t.meetup.point.lat, t.meetup.point.lng] : null,
    withWhom: t.them.displayName,
    /*
     * How far they are, in DistanceTag's three states — the same reading as a
     * listing's `km` above: undefined unknown, null below the floor, else km.
     * The server coarsened it; nothing here makes it more precise.
     */
    km: !t.them.distance || !t.them.distance.known ? undefined
      : t.them.distance.metres == null ? null : t.them.distance.metres / 1000,
    // The game of the card asked for, for a back office scoped to several.
    game: t.game || null,
    /*
     * Their shop, where they have a verified one — which makes "meet at their
     * shop" one tap in both meetup dialogs.
     */
    theirShop: t.them.shop || null,
    /*
     * ONE SIDE, THEN THE OTHER — never interleaved.
     *
     * The card draws an arrow per row and nothing else, so the only thing
     * separating "mine" from "theirs" is where the arrows sit. Reported by the
     * owner with a screenshot of exactly the wrong shape: a card he was getting,
     * then a card he was giving, then cash he was getting. Three rows, two
     * changes of direction, and you have to read every arrow to work out what
     * the deal is.
     *
     * The interleaving came from appending cash at the END regardless of which
     * way it flows. Cash IS part of one side of the trade — money you hand over
     * belongs beside the card you hand over — so it now sits with its own side
     * rather than after everything.
     *
     * GIVE FIRST, in the owner's own words for this: "I give these in exchange
     * of you giving me these things."
     */
    items: (() => {
      const rows = (t.items || []).map((i) => ({
        name: i.grade ? `${i.title} · ${i.grade}` : i.title,
        direction: i.direction,
        note: i.note || undefined,
      }));
      const cash = t.cash
        ? { name: 'Cash', direction: t.cash.youPay ? 'out' : 'in', note: formatCents(t.cash.cents) }
        : null;
      const side = (d) => rows.filter((r) => r.direction === d)
        .concat(cash && cash.direction === d ? [cash] : []);
      return side('out').concat(side('in'));
    })(),
    /*
     * WHOSE MOVE IT IS, straight from the server. Migration 065.
     *
     * Not derived from `role`, which is what every screen did before counters
     * and which quietly becomes wrong the moment a proposal is answered with
     * another proposal.
     */
    yourTurn: Boolean(t.yourTurn),
    /*
     * The offer as DATA, beside `items` which is the offer as DISPLAY.
     *
     * `items` above appends the grade into the name and folds cash in as a row,
     * which is right for reading and useless for editing: a counter dialog needs
     * the title on its own, and the cash as a number with a side. Two shapes for
     * two jobs beats one shape that does neither well — and re-deriving a title
     * by splitting "Charizard · NM" back apart is how a card called "Vol. 2 · 4"
     * eventually loses half its name.
     */
    offer: {
      items: (t.items || []).map((i) => ({
        id: i.id, title: i.title, grade: i.grade || null, direction: i.direction,
        // The card itself, which is what tells two items of one name apart.
        cardId: i.cardId || null,
        // The listing that card is on, so a shop's row can open it. Null when none.
        listingId: i.listingId || null,
      })),
      cash: t.cash || null,
      /*
       * The price this trade was agreed at, fixed when it was proposed so a
       * later edit to the listing cannot move it. See migration 074. Null for a
       * listing offered for trade and for trades proposed before that.
       */
      agreedPrice: t.agreedPrice || null,
    },
    /*
     * The offer the last counter swept away, so the person answering can see
     * whether the number moved up or down. Null where there is nothing honest to
     * compare against: no counter yet, or one made before the server recorded
     * what it replaced.
     */
    previousOffer: t.previousOffer ? {
      byYou: Boolean(t.previousOffer.byYou),
      items: (t.previousOffer.items || []).map((i) => ({
        title: i.title, grade: i.grade || null, cardId: i.cardId || null, direction: i.direction,
      })),
      cash: t.previousOffer.cash || null,
      quantity: t.previousOffer.quantity,
    } : null,
    updatedAt: t.updatedAt,
  };
}

/**
 * Day before month and a 24-hour clock in BOTH languages — that is a brand rule
 * rather than a locale convention, so the parts are assembled rather than taking
 * whatever order the locale would give.
 */
function formatMeetAt(iso) {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Europe/Madrid',
  }).formatToParts(d);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
  return `${get('weekday')} ${get('day')} ${get('month')} · ${get('hour')}:${get('minute')}`;
}

function formatCents(cents) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/**
 * WHAT A MONEY BOX MAY HOLD, and what that text is worth in cents.
 *
 * The other direction from `formatCents`, and the only other place in the app
 * where money and text meet. Everything else — the column, the API, every
 * `priceCents` on the way past — is an integer number of cents already.
 *
 * THE BUG THESE TWO REPLACE. Both money fields ran `replace(/[^0-9]/g, '')` on
 * every keystroke, so the separator was deleted as it was typed: «0,57» became
 * «057» and the cheapest card in a collection could not be listed at its own
 * price. Everything downstream already spoke cents; the box was the one thing
 * that could not say them.
 *
 * BOTH SEPARATORS, because a Spanish keyboard offers a comma and «0,57» is how
 * a price is written here, while a value pasted out of Cardmarket is «0.57».
 * Neither is normalised to the other on the way in — the box keeps the key that
 * was pressed, and the caret stays where the typist left it.
 *
 * TWO DECIMALS, and a third is DROPPED AS IT ARRIVES rather than rounded on the
 * way out. «1,005» is «1,00» in the box the moment the last key is pressed, so
 * what the field shows is what will be stored — a rounding that happened out of
 * sight, after the person stopped looking, is the version of this that produces
 * a listing at a price nobody typed.
 *
 * That truncation is also what settles an ambiguity `parseEuroCents` in
 * `api/src/lib/cardmarket-import.ts` has to resolve by guessing. It reads a
 * cell SCRAPED from a page in an unknown locale, so it must decide whether the
 * separator in «1,500» is a decimal point or a thousands mark, and its rule —
 * "it is grouping if and only if exactly three digits follow it" — is right
 * there and wrong here: in a box somebody is typing into, «1,500» has to be one
 * and a half, not fifteen hundred. So this is deliberately NOT that function. It
 * cannot be: a shared parse would have to hold both rules at once, and a fix to
 * either half would land in a screen it was never reasoned about. What is shared
 * is the reasoning, which is written down in both places.
 *
 * A SECOND SEPARATOR IS DECIDED BY SHAPE, and this is where the two do agree.
 * Only a paste produces one — «1.234,56», which has to survive as €1234,56 and
 * not become €1,23 — and it is grouping only if every group it fences off is
 * exactly three digits long. Where they are, the last separator is the decimal
 * point, which is `parseEuroCents`'s rule for the same string. Where they are
 * not, the FIRST separator stands and the rest are dropped, because outside a
 * paste a second separator is a slip: somebody who has typed «1,50» and then
 * catches the full stop meant «1,50», and reading the last mark as the decimal
 * point would hand them «150.» — a hundred and fifty euros, silently, from one
 * stray key.
 *
 * AND NO FLOAT, which is the same trap that comment names: `0.57 * 100` is
 * 56.999999999999996 and `+"0,57"` is NaN, and a NaN reaching `Math.round`
 * arrives at the API as a null the schema rejects, long after the typing. The
 * digits either side of the separator are read as two integers instead, so the
 * cents are counted rather than computed.
 */
function moneyText(raw) {
  const cleaned = String(raw == null ? '' : raw).replace(/[^0-9.,]/g, '');
  const marks = [];
  for (let i = 0; i < cleaned.length; i++) if (cleaned[i] === ',' || cleaned[i] === '.') marks.push(i);
  if (!marks.length) return cleaned;

  const groups = cleaned.split(/[.,]/);
  // Fenced by separators on BOTH sides — the head and the tail say nothing about
  // grouping, since "1.234,56" opens with one digit and ends with two.
  const grouping = marks.length > 1 && groups.slice(1, -1).every((g) => g.length === 3);
  const at = grouping ? marks[marks.length - 1] : marks[0];

  return cleaned.slice(0, at).replace(/[^0-9]/g, '')
    + cleaned[at]
    + cleaned.slice(at + 1).replace(/[^0-9]/g, '').slice(0, 2);
}

/**
 * Integer cents, or null where there is no number in the box at all — «», «,»
 * and «€» are all "nobody has said a price", which is not the same answer as 0.
 *
 * Sanitises its own input rather than trusting a caller to have done it. The
 * two-decimal rule above is what makes reading the fraction as an integer safe,
 * and a parse that assumed it instead of enforcing it would turn a stray third
 * digit into a silent factor of ten.
 */
function centsFromMoney(text) {
  const clean = moneyText(text);
  const at = Math.max(clean.lastIndexOf(','), clean.lastIndexOf('.'));
  const whole = at < 0 ? clean : clean.slice(0, at);
  const fraction = at < 0 ? '' : clean.slice(at + 1);
  if (!whole && !fraction) return null;
  return Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
}

/**
 * A conversation, shaped for the inbox.
 *
 * `fromYou` arrives already resolved by the API, so no screen has to compare ids
 * to work out which side of a conversation a line came from.
 */
function toConversationView(c) {
  return {
    id: c.id,
    with: c.with,
    unread: c.unread,
    preview: c.lastMessage ? c.lastMessage.body : '',
    lastFromYou: c.lastMessage ? c.lastMessage.fromYou : false,
    at: c.lastMessageAt ? formatWhen(c.lastMessageAt) : '',
  };
}

function toMessageView(m) {
  return {
    id: m.id, text: m.body, fromYou: m.fromYou,
    at: formatWhen(m.at),
    // The raw instant as well as the readable one. A formatted string cannot be
    // sorted against anything, and the conversation needs to interleave messages
    // with the trade that happened between two of them.
    atMs: Date.parse(m.at) || 0,
    // The listings this line shows as cards, in order — the server's answer.
    // Their cards travel beside the messages, keyed by id. Copied here or no
    // card ever renders: a field this mapper drops never reaches the screen.
    listings: Array.isArray(m.listings) ? m.listings : [],
  };
}

/**
 * A timestamp as a person reads it: a time today, a weekday this week, a date
 * beyond that. Day before month and a 24-hour clock in both languages, which is a
 * brand rule rather than a locale convention.
 */
export function formatWhen(iso) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const withinWeek = now - d < 6 * 86400000;
  const opts = sameDay
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : withinWeek
      ? { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }
      : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
  const parts = new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'Europe/Madrid' }).formatToParts(d);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
  const time = `${get('hour')}:${get('minute')}`;
  if (sameDay) return time;
  if (withinWeek) return `${get('weekday')} ${time}`;
  return `${get('day')} ${get('month')} · ${time}`;
}

/**
 * A link that opens the place in whatever maps app the phone already uses.
 *
 * A plain URL, not an API — no key, no bill, nothing sent anywhere until somebody
 * taps it. Coordinates when we have them, because a name alone can match the wrong
 * branch of a chain; the name rides along as the label.
 */
function mapsLink(place) {
  if (!place) return null;
  const q = place.lat != null && place.lng != null
    ? `${place.lat},${place.lng}`
    : place.name;
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

api.mapsLink = mapsLink;
api.toConversationView = toConversationView;
api.toMessageView = toMessageView;
api.toTradeView = toTradeView;
api.formatCents = formatCents;
api.moneyText = moneyText;
api.centsFromMoney = centsFromMoney;
/**
 * Exactly the props ListingCard takes, and nothing else.
 *
 * The card forwards what it does not recognise onto its <article>, which is the
 * right call for a design-system component — it is how a caller passes `id`,
 * `data-*` or `aria-*`. It only becomes a problem when a caller spreads a whole
 * view object at it: React then warns about every field the card has no name for
 * (`sellerHandle`, `sellerTrades`, `sellerFlagged`), and each one lands on a DOM
 * node in production.
 *
 * Naming the props here rather than spreading also means a field added to the API
 * tomorrow cannot silently end up as an HTML attribute.
 */
/*
 * `price` and `currency` are deliberately NOT here any more. The card composed
 * them itself as `currency + price` — symbol first, no space, no minor units —
 * and took a euro float to do it. The money now arrives as one finished string in
 * `priceLabel`, built below by the only thing that knows the reader's locale.
 */
/**
 * One entry out of a `srcset`, for a caller that can only take a single `src`.
 *
 * NEEDED FOR EXACTLY ONE REASON: `ListingCard` is a design-system component and
 * its image prop is a URL. It has no `srcset`, and this app does not own its
 * source — so the choice a browser would make has to be made here instead, once,
 * rather than by every screen that draws a card.
 *
 * THE SMALLEST COPY AT LEAST AS WIDE AS ASKED, and the widest available when
 * none is. Never the narrowest as a fallback: too few pixels is visible and too
 * many is only bytes, so the failure this rounds towards is the invisible one.
 *
 * Returns null for a null or unparseable srcset, which is what a picture with no
 * variants sends and what every caller must already handle.
 */
function pickWidth(srcset, want) {
  if (!srcset) return null;
  const entries = String(srcset).split(',')
    .map((part) => part.trim().split(/\s+/))
    .filter((p) => p.length === 2 && /^\d+w$/.test(p[1]))
    .map((p) => ({ url: p[0], width: parseInt(p[1], 10) }))
    .sort((a, b) => a.width - b.width);
  if (!entries.length) return null;
  return (entries.find((e) => e.width >= want) || entries[entries.length - 1]).url;
}

const CARD_PROPS = ['id', 'title', 'set', 'condition', 'language', 'foil', 'km', 'image', 'mode'];

/**
 * The words on the card, as well as the facts.
 *
 * `t` is REQUIRED, and that is the point of it. Every string a listing card shows
 * is drawn by the design system — the badge, the grade, the heart's accessible
 * name, the placeholder where there is no photo, the sub-kilometre floor — so
 * unless the app hands each one in, the card renders the component's own English
 * to a Spanish reader. It did, on all sixty cards of a browse page, while every
 * check we have reported a clean sweep.
 *
 * Folded into this function rather than offered as a second one a caller could
 * forget. There are five call sites; a missing translator throws on the first
 * render and is caught by any run of the suite, whereas a forgotten second spread
 * renders perfectly and is simply in the wrong language — which is the failure
 * this whole change exists to stop being invisible.
 */
function listingCardProps(listing, t) {
  const out = {};
  for (const key of CARD_PROPS) if (listing[key] !== undefined) out[key] = listing[key];
  /*
   * THE CARD'S PICTURE, at the size a card actually draws it.
   *
   * A browse tile is 199 CSS px on the desktop grid and 171 on a phone; a
   * full-size One Piece artwork averages 348 KB, so a page of 24 was pulling
   * about 8 MB to render stamps. 480 is that tile on a retina phone, which is
   * what Spain is holding.
   *
   * HERE AND NOT IN THE VIEW, because `ListingCard` is a design-system component
   * whose image prop is a URL — there is no `srcset` to hand it, and this app
   * does not own its source. So the choice a browser would make is made once, at
   * the only seam that feeds that component, and `listing.image` stays the
   * original for the screens that need it.
   *
   * `imageSrcset` is deliberately not added to CARD_PROPS: everything in that
   * list is spread onto the card's `<article>`, and a srcset on an article is
   * markup that means nothing.
   */
  const scaled = pickWidth(listing.imageSrcset, 480);
  if (scaled) out.image = scaled;
  // `mode` still picks the badge's TONE inside the card; only the words are ours.
  out.modeLabel = t(LISTING_MODE_NAMES[listing.mode] || LISTING_MODE_NAMES.sale);
  /*
   * The word for foil, like every other word this card renders.
   *
   * Foil was stored, sent by the API and shown on the listing page — and nowhere
   * else. So a browse grid, a search result and the card you pick in a counter
   * all showed a foil and a non-foil of one printing as the same thing, which
   * they are not: different objects, different prices, and the difference only
   * visible once somebody had clicked through.
   */
  out.foilLabel = t('Foil');
  // The grade's NOTATION (NM) is the trade's own and is not translated; the words
  // beside it are. An unknown grade falls back to itself rather than to English.
  out.conditionLabel = t(GRADE_NAMES[listing.condition] || listing.condition || GRADE_NAMES.NM);
  /*
   * WHAT THE EMPTY FRAME SAYS, and it has to say the right missing thing.
   *
   * It read "card photo", which names the slot rather than the state — and the
   * frame now appears for a second reason where it used to appear for one. A
   * listing shows no picture when nobody photographed the card, and ALSO when we
   * cannot say which drawing the card is: the catalogue keeps one picture per
   * card CODE, so all ten products of ST15-002 carry the OP-10 alternate art,
   * and showing it put a card that set never printed on somebody's advert. A
   * blank frame is a true statement about what we know; a confident wrong card
   * is not.
   *
   * So the words say the picture is missing. The listing page says the same
   * thing at greater length, where there is room for it — see listing.jsx.
   */
  out.photoLabel = t("No picture");
  out.distanceFloorLabel = t("less than 1 km");
  /*
   * The money, finished. Not a number and a symbol for the card to arrange —
   * where the symbol goes, which separator the decimal takes, and whether there
   * are minor units at all are three answers only the locale has, and the design
   * system is not told the locale.
   *
   * `priceCents` is CENTS: `formatCents` divides. Passing euros here is the 100×
   * bug and it renders something that looks like a price.
   */
  if (listing.priceCents != null) out.priceLabel = formatCents(listing.priceCents);
  return out;
}

api.toListingView = toListingView;
api.listingCardProps = listingCardProps;

